import { api } from '../../../config';
import { CargoType } from '../../../constants/masterData';
import { convertToDisplayDateFormatddmmmyyyy } from '../../../utils/dateformatter';
import {
  getPricingPeriodDetailLabel,
  getPricingPeriodDetailValue,
  getPricingPeriodLabel,
} from './pricingPeriodHelpers';

export const displayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

export const pick = (obj, camelKey, pascalKey) =>
  obj?.[camelKey] ?? obj?.[pascalKey] ?? null;

export const resolveId = obj => pick(obj, 'id', 'Id');

export const getCargoTypeLabel = cargoType => {
  if (cargoType === null || cargoType === undefined || cargoType === '') {
    return null;
  }
  const value = Number(cargoType);
  const option = CargoType[0]?.options?.find(item => item.value === value);
  return option?.label ?? null;
};

export const normalizeDeliveryItems = data => {
  if (!data) return [];
  const items = data.items || data.Items;
  if (Array.isArray(items)) {
    return items
      .map(item => {
        const id = resolveId(item);
        return id ? { ...item, id } : null;
      })
      .filter(Boolean);
  }
  const id = resolveId(data);
  if (id) return [{ ...data, id }];
  return [];
};

export const unwrapListItems = payload => {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.Items)) return payload.Items;
  if (Array.isArray(payload)) return payload;
  if (payload.data) return unwrapListItems(payload.data);
  if (payload.Data) return unwrapListItems(payload.Data);
  return [];
};

const getDeliveryDetailGroups = result =>
  result?.data?.data || result?.data?.Data || [];

const getListData = item => item?.listData || item?.ListData || [];

const pickNamedValue = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      null
    );
  }
  return value;
};

const normalizeCellValue = value => {
  const resolved = pickNamedValue(value) ?? value;
  if (resolved === null || resolved === undefined) return '-';
  const text = String(resolved).trim();
  return text || '-';
};

const mapIncotermRow = details => ({
  deliveryBasis: normalizeCellValue(
    pick(details, 'deliveryBasis', 'DeliveryBasis')
  ),
  parcelSize: normalizeCellValue(
    pick(details, 'parcelSize', 'ParcelSize') ??
      pick(details, 'deliveryBasis', 'DeliveryBasis')
  ),
  incoterm: normalizeCellValue(pick(details, 'incoterm', 'Incoterm')),
  route: normalizeCellValue(pick(details, 'route', 'Route')),
  loadingPort: normalizeCellValue(pick(details, 'loadingPort', 'LoadingPort')),
  dischargeQty1: normalizeCellValue(
    pick(details, 'parcelSize', 'ParcelSize') ??
      pick(details, 'deliveryBasis', 'DeliveryBasis')
  ),
  port1: normalizeCellValue(pick(details, 'port1', 'Port1')),
  aldAdd1: normalizeCellValue(pick(details, 'addAld', 'AddAld')),
  dischargeQty2: normalizeCellValue(
    pick(details, 'parcelSize2', 'ParcelSize2')
  ),
  port2: normalizeCellValue(pick(details, 'port2', 'Port2')),
  aldAdd2: normalizeCellValue(pick(details, 'addAld2', 'AddAld2')),
  dischargeQty3: normalizeCellValue(
    pick(details, 'parcelSize3', 'ParcelSize3')
  ),
  port3: normalizeCellValue(pick(details, 'port3', 'Port3')),
  aldAdd3: normalizeCellValue(pick(details, 'addAld3', 'AddAld3')),
  alpha: normalizeCellValue(
    pick(details, 'alphaMitra1', 'AlphaMitra1') ??
      pick(details, 'alpha1', 'Alpha1')
  ),
  // Alpha HPS dyed/undyed jalur non-LPG. Nilai baris (Straits) dibiarkan mentah -
  // bukan normalizeCellValue - supaya `null` tetap `null` dan konsumen bisa
  // membedakan "belum diisi" dari nilai 0. Nilai per lokasi ada di
  // incotermFOBLocations[] yang sudah lolos utuh lewat mapIncotermTimelineItem.
  alphaDyedHps: pick(details, 'alphaDyedHps', 'AlphaDyedHps') ?? null,
  alphaUndyedHps: pick(details, 'alphaUndyedHps', 'AlphaUndyedHps') ?? null,
  // Diteruskan utuh karena hanya object lokasi dari get-delivery-detail yang membawa
  // alpha HPS per lokasi; daftar lokasi dari /quantity-incoterm tidak memuatnya.
  incotermFOBLocations: Array.isArray(details?.incotermFOBLocations)
    ? details.incotermFOBLocations
    : [],
  quantityIncotermId: pick(details, 'quantityIncotermId', 'QuantityIncotermId'),
});

const mapIncotermRows = result => {
  const groups = getDeliveryDetailGroups(result);
  return groups.flatMap(item => getListData(item).map(mapIncotermRow));
};

const mapIncotermRowFromQuantityIncoterm = item => ({
  deliveryBasis: normalizeCellValue(
    pick(item, 'deliveryBasis', 'DeliveryBasis')
  ),
  parcelSize: normalizeCellValue(
    pick(item, 'parcelSize', 'ParcelSize') ??
      pick(item, 'deliveryBasis', 'DeliveryBasis')
  ),
  incoterm: normalizeCellValue(pick(item, 'incoterm', 'Incoterm')),
  route: normalizeCellValue(
    pickNamedValue(pick(item, 'incotermRoute', 'IncotermRoute')) ??
      pick(item, 'incotermPortType', 'IncotermPortType')
  ),
  loadingPort: normalizeCellValue(
    pickNamedValue(pick(item, 'loadingPort', 'LoadingPort'))
  ),
  dischargeQty1: normalizeCellValue(
    pick(item, 'parcelSize', 'ParcelSize') ??
      pick(item, 'deliveryBasis', 'DeliveryBasis')
  ),
  port1: normalizeCellValue(
    pick(item, 'incotermLocation', 'IncotermLocation') ??
      pick(item, 'port1', 'Port1')
  ),
  aldAdd1: normalizeCellValue(pick(item, 'timelineType', 'TimelineType')),
  dischargeQty2: normalizeCellValue(pick(item, 'parcelSize2', 'ParcelSize2')),
  port2: normalizeCellValue(
    pick(item, 'incotermLocation2', 'IncotermLocation2') ??
      pick(item, 'port2', 'Port2')
  ),
  aldAdd2: normalizeCellValue(
    [
      convertToDisplayDateFormatddmmmyyyy(pick(item, 'startDate', 'StartDate')),
      convertToDisplayDateFormatddmmmyyyy(pick(item, 'endDate', 'EndDate')),
    ]
      .filter(value => value && value !== '-')
      .join(' - ') || pick(item, 'addAld', 'AddAld')
  ),
  dischargeQty3: normalizeCellValue(pick(item, 'parcelSize3', 'ParcelSize3')),
  port3: normalizeCellValue(
    pick(item, 'incotermLocation3', 'IncotermLocation3') ??
      pick(item, 'port3', 'Port3')
  ),
  aldAdd3: normalizeCellValue(pick(item, 'addAld3', 'AddAld3')),
  alpha: normalizeCellValue(
    pick(item, 'alpha', 'Alpha') ??
      pick(item, 'alphaMitra1', 'AlphaMitra1') ??
      pick(item, 'alpha1', 'Alpha1')
  ),
  quantityIncotermId: resolveId(item),
});

export const buildIncotermRows = (
  deliveryDetailResult,
  rawIncotermItems = []
) => {
  const deliveryRows = deliveryDetailResult
    ? mapIncotermRows(deliveryDetailResult)
    : [];

  if (deliveryRows.length >= rawIncotermItems.length) {
    return deliveryRows;
  }

  if (!deliveryRows.length) {
    return rawIncotermItems.map(mapIncotermRowFromQuantityIncoterm);
  }

  const supplementalRows = rawIncotermItems
    .slice(deliveryRows.length)
    .map(mapIncotermRowFromQuantityIncoterm);

  return [...deliveryRows, ...supplementalRows];
};

export const mapIncotermTimelineItem = item => ({
  incoterm:
    pickNamedValue(pick(item, 'incoterm', 'Incoterm')) ??
    pick(item, 'incoterm', 'Incoterm'),
  incotermPortType: pick(item, 'incotermPortType', 'IncotermPortType'),
  incotermRoute: pickNamedValue(pick(item, 'incotermRoute', 'IncotermRoute')),
  incotermFOBLocations: Array.isArray(item?.incotermFOBLocations)
    ? item.incotermFOBLocations
    : item?.incotermFOBLocations
      ? [item.incotermFOBLocations]
      : [],
  incotermLocation: pickNamedValue(
    pick(item, 'incotermLocation', 'IncotermLocation')
  ),
  startDate: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'startDate', 'StartDate')
  ),
  endDate: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'endDate', 'EndDate')
  ),
  incotermLocation2: pickNamedValue(
    pick(item, 'incotermLocation2', 'IncotermLocation2')
  ),
  startDate2: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'startDate2', 'StartDate2')
  ),
  endDate2: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'endDate2', 'EndDate2')
  ),
  incotermLocation3: pickNamedValue(
    pick(item, 'incotermLocation3', 'IncotermLocation3')
  ),
  startDate3: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'startDate3', 'StartDate3')
  ),
  endDate3: convertToDisplayDateFormatddmmmyyyy(
    pick(item, 'endDate3', 'EndDate3')
  ),
  timelineType: pick(item, 'timelineType', 'TimelineType'),
  quantityIncotermId: resolveId(item),
});

export const fetchIncotermTimeline = async (quantityDeliveryTermId, token) => {
  if (!quantityDeliveryTermId || !token) {
    return { rawItems: [], timelineItems: [] };
  }

  const response = await fetch(
    `${api.API_URL}/quantity-incoterm?QuantityDeliveryTermId=${quantityDeliveryTermId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) return { rawItems: [], timelineItems: [] };

  const result = await response.json();
  const rawItems = unwrapListItems(result.data ?? result);

  return {
    rawItems,
    timelineItems: rawItems.map(mapIncotermTimelineItem),
  };
};

export const getDeliveryGroup = (result, quantityDeliveryTermId) => {
  const groups = getDeliveryDetailGroups(result);
  if (!groups.length) return null;
  return (
    groups.find(group => resolveId(group) === quantityDeliveryTermId) ||
    groups[0]
  );
};

export const mapParcelFromSources = (quantityItem, deliveryGroup) => {
  const merged = {
    ...quantityItem,
    ...(deliveryGroup || {}),
    deliveryUnit: quantityItem?.deliveryUnit ?? deliveryGroup?.deliveryUnit,
  };

  return {
    id: resolveId(merged) ?? resolveId(quantityItem),
    deliveryUnit: merged.deliveryUnit,
    frequency: merged.frequency,
    cargoType: merged.cargoType ?? null,
    cargoTypeLabel:
      getCargoTypeLabel(merged.cargoType) ??
      pick(merged.cargoType, 'name', 'Name'),
    ratioLpg: merged.ratioLpg?.name ?? merged.ratioLpg ?? null,
    pricingPeriod: merged.pricingPeriod,
    pricingPeriodLabel: getPricingPeriodLabel(merged.pricingPeriod),
    pricingPeriodDetailLabel: getPricingPeriodDetailLabel(merged.pricingPeriod),
    pricingPeriodDetail: getPricingPeriodDetailValue(merged),
    indexPrice: merged.indexPrice?.name ?? merged.indexPrice ?? null,
    otherIndexPrice:
      merged.otherIndexPrice?.name ?? merged.otherIndexPrice ?? null,
    notesForPricing: merged.notesForPricing,
    isCorrelationBetweenTwoIndex: merged.isCorrelationBetweenTwoIndex === true,
    quantities: (
      deliveryGroup?.listQuantityInformation ||
      deliveryGroup?.ListQuantityInformation ||
      []
    ).map(item => ({
      id: resolveId(item),
      quantity: pick(item, 'quantity', 'Quantity'),
      cargoRatios: (pick(item, 'cargoRatios', 'CargoRatios') || []).map(
        ratio => ({
          id: pick(ratio, 'id', 'Id'),
          cargoRatio: pick(ratio, 'cargoRatio', 'CargoRatio'),
          cargoRatioLabel: pick(ratio, 'cargoRatioLabel', 'CargoRatioLabel'),
          parcelSize: pick(ratio, 'parcelSize', 'ParcelSize') ?? null,
          alpha: pick(ratio, 'alpha', 'Alpha') ?? null,
        })
      ),
    })),
    incotermRows: [],
    incotermTimelineItems: [],
  };
};

export const unwrapDeliveryDetail = response => {
  if (!response) return {};
  const payload = response.data ?? response;
  if (resolveId(payload) || payload?.deliveryUnit) {
    return payload;
  }
  return response.data || {};
};
